export type CategoryType =
  | 'Top Rated'
  | 'Marketing & Sales'
  | 'Business & Leadership'
  | 'Creative & Narrative'
  | 'Persuasion & Public Speaking'
  | 'Data & Presentation'

export interface Step {
  title: string
  description: string
}

export interface Framework {
  id: string
  name: string
  category: CategoryType
  origin: string
  summary: string
  description: string
  steps: Step[]
  bestFor: string[]
  example: string
}

export const categories: CategoryType[] = [
  'Top Rated',
  'Marketing & Sales',
  'Business & Leadership',
  'Creative & Narrative',
  'Persuasion & Public Speaking',
  'Data & Presentation',
]

export const categoryColors: Record<CategoryType, string> = {
  'Top Rated':
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  'Marketing & Sales':
    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  'Business & Leadership':
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  'Creative & Narrative':
    'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800',
  'Persuasion & Public Speaking':
    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
  'Data & Presentation':
    'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
}

export const frameworks: Framework[] = [
  // ⭐ Top Rated
  {
    id: 'heros-journey',
    name: "The Hero's Journey",
    category: 'Top Rated',
    origin: 'Joseph Campbell',
    summary:
      'A classic narrative structure where a protagonist goes on an adventure, learns a lesson, and returns transformed.',
    description:
      'Also known as the monomyth, this framework was popularized by Joseph Campbell in his book "The Hero with a Thousand Faces". It describes the common template of a broad category of tales and lore that involves a hero who goes on an adventure, and in a decisive crisis wins a victory, and then comes home changed or transformed. It is the underlying structure of countless myths, legends, and modern blockbusters like Star Wars and The Matrix.',
    steps: [
      {
        title: 'Departure',
        description:
          'The hero lives in the ordinary world, receives a call to adventure, initially refuses, meets a mentor, and crosses the threshold into the unknown.',
      },
      {
        title: 'Initiation',
        description:
          'The hero faces trials, makes allies and enemies, approaches the innermost cave, endures the supreme ordeal, and seizes the reward.',
      },
      {
        title: 'Return',
        description:
          'The hero travels back to the ordinary world, experiences a final resurrection/transformation, and returns with the elixir to help their world.',
      },
    ],
    bestFor: ['Fiction', 'Brand Storytelling', 'Long-form Content', 'Film'],
    example:
      'Star Wars: Luke Skywalker (ordinary world) meets Obi-Wan (mentor), leaves Tatooine (crosses threshold), rescues Leia and destroys the Death Star (ordeal/reward), and becomes a Jedi (transformation).',
  },
  {
    id: 'storybrand',
    name: 'StoryBrand',
    category: 'Top Rated',
    origin: 'Donald Miller',
    summary:
      'Positions the customer as the hero and your brand as the guide to help them achieve success.',
    description:
      'The StoryBrand framework flips traditional marketing on its head. Instead of the brand being the hero of the story, the customer is the hero. The brand plays the role of the "Guide" who provides a plan to help the hero overcome their problems and achieve success. This clarifies messaging and makes marketing materials significantly more engaging because people are naturally drawn to stories about themselves.',
    steps: [
      {
        title: 'A Character',
        description:
          'The customer is the hero, and they want something specific.',
      },
      {
        title: 'Has a Problem',
        description:
          'The hero encounters a problem (villain, external, internal, philosophical) preventing them from getting what they want.',
      },
      {
        title: 'And Meets a Guide',
        description:
          'The hero meets your brand, who demonstrates empathy and authority.',
      },
      {
        title: 'Who Gives Them a Plan',
        description:
          'The guide provides a clear, simple plan to overcome the problem.',
      },
      {
        title: 'And Calls Them to Action',
        description: 'The guide challenges the hero to take action.',
      },
      {
        title: 'That Ends in Success',
        description:
          "Show what the hero's life looks like if they use your product.",
      },
      {
        title: 'And Helps Them Avoid Failure',
        description:
          "Show what the hero's life looks like if they don't use your product.",
      },
    ],
    bestFor: [
      'Website Copy',
      'Marketing Campaigns',
      'Sales Pitches',
      'Brand Messaging',
    ],
    example:
      'A financial advisor website: "You want to retire comfortably (Character/Desire). But confusing markets make you anxious (Problem). We are certified planners with 20 years experience (Guide). Follow our 3-step wealth plan (Plan). Schedule a call today (CTA) to secure your family\'s future (Success) and avoid working past 70 (Failure)."',
  },
  {
    id: 'pixar-story-spine',
    name: "Pixar's Story Spine",
    category: 'Top Rated',
    origin: 'Kenn Adams / Pixar',
    summary:
      'A simple, fill-in-the-blank formula that guarantees a complete narrative arc with a clear beginning, middle, and end.',
    description:
      'Originally created by playwright Kenn Adams and famously adopted by Pixar Animation Studios, the Story Spine is a structural tool that helps writers ensure their story has a solid foundation. It forces you to establish a status quo, introduce an inciting incident, escalate the consequences, and resolve the narrative. If your story cannot fit into this spine, it likely lacks a fundamental narrative drive.',
    steps: [
      {
        title: 'Once upon a time...',
        description: 'Establish the setting and the protagonist.',
      },
      {
        title: 'Every day...',
        description:
          "Establish the status quo and the protagonist's normal life.",
      },
      {
        title: 'One day...',
        description: 'The inciting incident that breaks the status quo.',
      },
      {
        title: 'Because of that...',
        description: 'The immediate consequence of the inciting incident.',
      },
      {
        title: 'Because of that...',
        description: 'The escalating consequences (repeat as needed).',
      },
      {
        title: 'Until finally...',
        description: 'The climax where the conflict is resolved.',
      },
      {
        title: 'And ever since then...',
        description: 'The new normal and the moral of the story.',
      },
    ],
    bestFor: [
      'Pitching Ideas',
      'Short Stories',
      'Presentations',
      'User Journeys',
    ],
    example:
      'Finding Nemo: Once upon a time, there was a widowed fish named Marlin. Every day, he overprotected his son Nemo. One day, Nemo was captured by a diver. Because of that, Marlin swam across the ocean. Because of that, he met Dory and faced sharks and jellyfish. Until finally, he learned to let go and rescued Nemo. And ever since then, Marlin trusted his son to explore the world.',
  },
  {
    id: 'freytags-pyramid',
    name: "Freytag's Pyramid",
    category: 'Top Rated',
    origin: 'Gustav Freytag',
    summary:
      'A dramatic structural framework that divides a story into five distinct parts, resembling a pyramid.',
    description:
      'Developed by 19th-century German novelist Gustav Freytag, this framework analyzes the structure of classical Greek and Shakespearean dramas. It visualizes the narrative arc as a pyramid, starting with exposition, rising to a climax, and falling to a resolution. It remains one of the most widely taught narrative structures in literature and creative writing.',
    steps: [
      {
        title: 'Exposition',
        description:
          'Introduction of characters, setting, and background information.',
      },
      {
        title: 'Inciting Incident',
        description: 'The event that sets the main plot in motion.',
      },
      {
        title: 'Rising Action',
        description:
          'A series of events building tension and developing the conflict.',
      },
      {
        title: 'Climax',
        description:
          'The turning point of the story; the moment of highest tension.',
      },
      {
        title: 'Falling Action',
        description:
          'The conflict begins to resolve, and the fallout from the climax is dealt with.',
      },
      {
        title: 'Resolution / Denouement',
        description:
          'The final outcome where loose ends are tied up and a new normal is established.',
      },
    ],
    bestFor: ['Novels', 'Plays', 'Screenplays', 'Case Studies'],
    example:
      'Romeo and Juliet: The Montagues and Capulets feud (Exposition). Romeo and Juliet meet (Inciting Incident). They marry secretly (Rising Action). Romeo kills Tybalt and is banished (Climax). Juliet fakes her death (Falling Action). They both die, ending the family feud (Resolution).',
  },

  // 📣 Marketing & Sales
  {
    id: 'aida',
    name: 'AIDA',
    category: 'Marketing & Sales',
    origin: 'Elias St. Elmo Lewis',
    summary:
      'A classic marketing funnel model tracing the customer journey from first awareness to final action.',
    description:
      "Developed in 1898, AIDA is one of the foundational models in advertising and marketing. It describes the cognitive stages an individual goes through during the buying process for a product or service. It is highly effective for structuring landing pages, sales emails, and advertisements, ensuring that you don't ask for the sale before you've built sufficient interest and desire.",
    steps: [
      {
        title: 'Attention',
        description:
          "Catch the reader's eye with a bold headline, surprising fact, or striking image.",
      },
      {
        title: 'Interest',
        description:
          'Keep them engaged with fascinating information, storytelling, or addressing their specific problems.',
      },
      {
        title: 'Desire',
        description:
          'Bridge the gap between their problem and your solution. Show benefits, social proof, and make them want what you have.',
      },
      {
        title: 'Action',
        description:
          'Tell them exactly what to do next with a clear, compelling Call to Action (CTA).',
      },
    ],
    bestFor: [
      'Landing Pages',
      'Email Marketing',
      'Advertisements',
      'Sales Letters',
    ],
    example:
      'A fitness app ad: "Tired of diets that don\'t work?" (Attention). "Our app uses AI to adapt to your metabolism." (Interest). "Join 10,000 users who lost an average of 15lbs in 2 months. See their photos." (Desire). "Download now for a free 7-day trial." (Action).',
  },
  {
    id: 'pas',
    name: 'PAS (Problem, Agitate, Solve)',
    category: 'Marketing & Sales',
    origin: 'Dan Kennedy (popularized)',
    summary:
      'A highly persuasive copywriting formula that focuses on a pain point before offering the solution.',
    description:
      'PAS is arguably the most reliable copywriting framework ever created. It works because human beings are naturally more motivated to avoid pain than to seek pleasure. By clearly identifying a problem and then "agitating" it (reminding the reader of how frustrating, expensive, or painful that problem is), you create a strong emotional desire for the solution you are about to present.',
    steps: [
      {
        title: 'Problem',
        description:
          'Identify and clearly state the specific problem your target audience is facing.',
      },
      {
        title: 'Agitate',
        description:
          'Pour salt in the wound. Describe the emotional, financial, or time-based costs of not solving the problem. Make it hurt.',
      },
      {
        title: 'Solve',
        description:
          'Present your product or service as the ultimate, reliable solution to the agitated problem.',
      },
    ],
    bestFor: [
      'Social Media Ads',
      'Cold Emails',
      'Sales Pages',
      'Blog Introductions',
    ],
    example:
      'A productivity tool: "Are you drowning in unread emails?" (Problem). "Every hour spent organizing your inbox is an hour stolen from deep work, family time, and actual business growth. You\'re falling behind." (Agitate). "InboxZero AI automatically sorts, replies, and archives your emails so you reach zero every day in 5 minutes." (Solve).',
  },
  {
    id: 'bab',
    name: 'Before-After-Bridge',
    category: 'Marketing & Sales',
    origin: 'Direct Response Copywriting',
    summary:
      'Paints a picture of the current painful reality, the ideal future, and how to get there.',
    description:
      'The BAB framework is incredibly effective because it focuses heavily on the transformation. Instead of getting bogged down in features, it forces the writer to focus on the user\'s state of being. It contrasts the negative "Before" state with the highly desirable "After" state, positioning your product as the only logical bridge between the two.',
    steps: [
      {
        title: 'Before',
        description:
          "Describe the reader's current world, complete with all its frustrations, inefficiencies, and pain points.",
      },
      {
        title: 'After',
        description:
          'Describe a world where those problems are completely solved. How does it feel? What is possible now?',
      },
      {
        title: 'Bridge',
        description:
          'Introduce your product, service, or idea as the exact path to get from the Before to the After.',
      },
    ],
    bestFor: [
      'Cold Outreach',
      'Landing Page Headers',
      'Social Media Posts',
      'Case Studies',
    ],
    example:
      'A sleep supplement: "You toss and turn for hours, waking up groggy and relying on 4 cups of coffee just to survive the workday." (Before). "Imagine falling asleep in 10 minutes, sleeping deeply through the night, and waking up energized before your alarm rings." (After). "Our natural melatonin-magnesium blend is the key to unlocking the best sleep of your life." (Bridge).',
  },
  {
    id: 'star-chain-hook',
    name: 'Star-Chain-Hook',
    category: 'Marketing & Sales',
    origin: 'Frank Dignan',
    summary:
      'A framework for writing persuasive messages that grab attention, build a logical case, and demand action.',
    description:
      'Developed by Dr. Frank Dignan, this framework is particularly useful for direct mail and email marketing. It ensures that your message doesn\'t just sound good, but is built on a solid foundation of proof. The "Chain" is the critical element here, requiring the writer to link facts, benefits, and testimonials together to create an unbreakable logical argument.',
    steps: [
      {
        title: 'Star',
        description:
          'The big, bold opening that grabs attention immediately (a startling fact, a big promise, a provocative question).',
      },
      {
        title: 'Chain',
        description:
          'A series of facts, benefits, reasons, and proofs that logically link the Star to the Hook. This builds credibility and desire.',
      },
      {
        title: 'Hook',
        description:
          'The powerful call to action that reels them in, making it easy and urgent to take the next step.',
      },
    ],
    bestFor: [
      'Email Newsletters',
      'Direct Mail',
      'Sales Letters',
      'Pitch Decks',
    ],
    example:
      '"You can double your reading speed in 7 days." (Star). "Here is the science behind peripheral vision reading. Here are 3 testimonials from CEOs who took our course. Here is a demonstration video showing a student reading 800 WPM." (Chain). "Click here to claim your 50% discount before the class fills up tonight." (Hook).',
  },
  {
    id: '4-ps',
    name: 'The 4 Ps',
    category: 'Marketing & Sales',
    origin: 'Henry Hoke / Ray Edwards',
    summary:
      'A comprehensive copywriting formula: Promise, Picture, Proof, Push.',
    description:
      'Not to be confused with the 4 Ps of Marketing (Product, Price, Place, Promotion), this is a copywriting framework designed to guide a reader through a persuasive narrative. It starts with a bold claim, helps the reader visualize the outcome, backs up the claim with hard evidence, and ends with a strong call to action.',
    steps: [
      {
        title: 'Promise',
        description:
          'Make a bold claim about how you will help the reader or solve their problem.',
      },
      {
        title: 'Picture',
        description:
          'Use vivid, sensory language to paint a picture of what their life will look like once the promise is fulfilled.',
      },
      {
        title: 'Proof',
        description:
          'Provide statistics, case studies, testimonials, or demonstrations to prove that your promise is real.',
      },
      {
        title: 'Push',
        description:
          'Deliver a compelling call to action, often with urgency or scarcity, to push them to act now.',
      },
    ],
    bestFor: [
      'Sales Pages',
      'Webinars',
      'Video Sales Letters (VSLs)',
      'Ad Copy',
    ],
    example:
      '"We guarantee to increase your website traffic by 50% in 90 days." (Promise). "Imagine waking up to an inbox full of inbound leads, never having to cold call again." (Picture). "Here are screenshots of our clients\' Google Analytics, and video testimonials from 5 founders." (Proof). "Apply for a free audit today—we only take 3 new clients per month." (Push).',
  },

  // 💼 Business & Leadership
  {
    id: 'scr',
    name: 'Situation-Complication-Resolution (SCR)',
    category: 'Business & Leadership',
    origin: 'McKinsey & Company',
    summary:
      'A structured way to communicate business problems and proposed solutions clearly and concisely.',
    description:
      'The SCR framework is a staple in management consulting and executive communication. It is designed to cut through the noise and deliver the "so what?" immediately. By establishing a shared understanding of the current state, introducing the specific problem, and immediately offering the solution, it respects the audience\'s time and focuses the conversation on action.',
    steps: [
      {
        title: 'Situation',
        description:
          'State the current, undisputed facts. Establish the baseline context that everyone agrees on.',
      },
      {
        title: 'Complication',
        description:
          'Introduce the problem, change, or challenge that has disrupted the Situation. Why do we need to act?',
      },
      {
        title: 'Resolution',
        description:
          'Present your proposed solution or the specific action required to address the Complication.',
      },
    ],
    bestFor: [
      'Executive Summaries',
      'Internal Memos',
      'Project Updates',
      'Consulting Reports',
    ],
    example:
      '"Our Q3 revenue grew by 15%, driven by our new software product." (Situation). "However, customer churn on the new product has spiked to 8% due to onboarding difficulties." (Complication). "We need to allocate $50k to hire two dedicated customer success managers immediately to revamp the onboarding flow." (Resolution).',
  },
  {
    id: 'golden-circle',
    name: 'The Golden Circle',
    category: 'Business & Leadership',
    origin: 'Simon Sinek',
    summary:
      'A leadership framework that argues inspiring organizations communicate from the inside out: Why, How, What.',
    description:
      'Popularized by Simon Sinek\'s famous TED Talk, "Start With Why," this framework explains why some organizations and leaders are able to inspire where others aren\'t. Most companies know WHAT they do, some know HOW they do it, but very few know WHY they do it. By communicating the "Why" first, you speak directly to the emotional and decision-making centers of the brain.',
    steps: [
      {
        title: 'Why',
        description:
          'Your core belief, purpose, or cause. Why does your organization exist? Why should anyone care?',
      },
      {
        title: 'How',
        description:
          'Your unique value proposition or process. How do you fulfill your "Why"?',
      },
      {
        title: 'What',
        description:
          'The actual products or services you sell. The tangible result of your "Why" and "How".',
      },
    ],
    bestFor: [
      'Company Vision',
      'Brand Strategy',
      'Leadership Speeches',
      'Recruiting',
    ],
    example:
      'Apple: "Everything we do, we believe in challenging the status quo and thinking differently." (Why). "We do this by making our products beautifully designed and simple to use." (How). "We just happen to make great computers. Want to buy one?" (What).',
  },
  {
    id: 'scqa',
    name: 'SCQA',
    category: 'Business & Leadership',
    origin: 'Barbara Minto',
    summary:
      'An extension of SCR that explicitly states the question the audience is asking before providing the answer.',
    description:
      'Part of the Minto Pyramid Principle, SCQA is used to structure the introduction of a business document or presentation. It ensures that the content you are about to present is highly relevant to the audience by explicitly stating the question that the Complication has triggered in their minds.',
    steps: [
      { title: 'Situation', description: 'The stable context.' },
      {
        title: 'Complication',
        description: 'The trigger or problem that disrupts the situation.',
      },
      {
        title: 'Question',
        description:
          'The specific question that arises from the complication (often implicit, but made explicit here).',
      },
      { title: 'Answer', description: 'Your main idea or proposed solution.' },
    ],
    bestFor: ['Whitepapers', 'Board Presentations', 'Strategic Proposals'],
    example:
      '"We are the market leader in physical retail." (Situation). "E-commerce competitors are eating into our margins." (Complication). "How can we transition to an omnichannel model without cannibalizing our stores?" (Question). "By launching a click-and-collect service integrated with our loyalty program." (Answer).',
  },
  {
    id: 'mckinsey-pyramid',
    name: 'The McKinsey Pyramid Principle',
    category: 'Business & Leadership',
    origin: 'Barbara Minto',
    summary:
      'A top-down communication strategy that starts with the main answer, followed by grouped supporting arguments.',
    description:
      'Created by Barbara Minto at McKinsey, this principle dictates that ideas should always be presented as a pyramid under a single thought. You give the executive summary or main answer first, then group your supporting arguments into logical categories, and finally provide the data to support those arguments. It prevents the "mystery novel" approach to business writing.',
    steps: [
      {
        title: 'The Core Message',
        description:
          'Start with the main answer, recommendation, or conclusion at the very top.',
      },
      {
        title: 'Key Arguments',
        description:
          'Support the core message with mutually exclusive, collectively exhaustive (MECE) arguments.',
      },
      {
        title: 'Supporting Data',
        description:
          'Provide the specific facts, data, and evidence that prove each key argument.',
      },
    ],
    bestFor: [
      'Analytical Reports',
      'Executive Briefings',
      'Complex Problem Solving',
    ],
    example:
      'Core: "We should acquire Company X." Arguments: "1. It expands our geographic reach. 2. It provides proprietary technology. 3. It is financially accretive." Data: "[Market share stats for geography], [Patent details], [ROI projections]."',
  },

  // 🎨 Creative & Narrative
  {
    id: 'three-act',
    name: 'Three-Act Structure',
    category: 'Creative & Narrative',
    origin: 'Aristotle / Syd Field',
    summary:
      'The foundational model of modern storytelling, dividing a narrative into Setup, Confrontation, and Resolution.',
    description:
      "Tracing its roots back to Aristotle's Poetics and modernized by screenwriting guru Syd Field, the Three-Act Structure is the blueprint for almost every modern film and novel. It provides a reliable pacing mechanism, ensuring that the story has a clear beginning, a messy and escalating middle, and a satisfying end.",
    steps: [
      {
        title: 'Act I: Setup',
        description:
          'Introduces the characters, the world, and the inciting incident that forces the protagonist into action. Ends with Plot Point 1.',
      },
      {
        title: 'Act II: Confrontation',
        description:
          'The longest part of the story. The protagonist faces obstacles, attempts to solve the problem, usually fails, and hits a low point. Ends with Plot Point 2.',
      },
      {
        title: 'Act III: Resolution',
        description:
          'The climax where the protagonist faces the final challenge with new knowledge, followed by the denouement (tying up loose ends).',
      },
    ],
    bestFor: ['Screenplays', 'Novels', 'Theatrical Plays', 'Long-form Videos'],
    example:
      "The Matrix: Neo learns about the Matrix and chooses the red pill (Act I). Neo trains, meets the Oracle, and the crew is betrayed, leading to Morpheus's capture (Act II). Neo rescues Morpheus, realizes he is the One, defeats Agent Smith, and embraces his power (Act III).",
  },
  {
    id: 'kishotenketsu',
    name: 'Kishōtenketsu',
    category: 'Creative & Narrative',
    origin: 'Classical East Asian Narrative',
    summary:
      'A four-act narrative structure that relies on a twist or shift in perspective rather than direct conflict.',
    description:
      'Unlike Western storytelling, which is heavily reliant on conflict and resolution (protagonist vs. antagonist), Kishōtenketsu is a traditional East Asian structure (used in Chinese, Korean, and Japanese narratives, including manga and Nintendo game design). It focuses on exposition, development, a sudden twist or new element, and a conclusion that ties the twist back to the beginning.',
    steps: [
      {
        title: 'Ki (Introduction)',
        description:
          'Establishes the characters, setting, and baseline situation.',
      },
      {
        title: 'Shō (Development)',
        description:
          'Expands upon the introduction. Things proceed normally without major conflict.',
      },
      {
        title: 'Ten (Twist)',
        description:
          'A sudden, unexpected event or new perspective is introduced. It may seem unrelated at first.',
      },
      {
        title: 'Ketsu (Conclusion)',
        description:
          'Reconciles the twist with the introduction and development, bringing harmony or a new understanding.',
      },
    ],
    bestFor: [
      'Short Stories',
      'Manga/Comics',
      'Game Level Design',
      'Slice-of-Life Narratives',
    ],
    example:
      'Super Mario Level Design: Introduce a new mechanic safely (Ki). Make the mechanic slightly harder (Shō). Add a twist, like combining it with enemies or a timer (Ten). Provide a final test before the flagpole (Ketsu).',
  },
  {
    id: 'moth-method',
    name: 'The Moth Method',
    category: 'Creative & Narrative',
    origin: 'The Moth (Storytelling Org)',
    summary:
      'A framework for telling true, personal stories live on stage, focusing on stakes and transformation.',
    description:
      'Developed by the famous live storytelling organization "The Moth," this framework is designed to make personal anecdotes compelling to an audience of strangers. It emphasizes that a story is not just a sequence of events ("this happened, then this happened"), but a narrative of personal change driven by clear stakes.',
    steps: [
      {
        title: 'The Hook',
        description: 'Start in the action. Grab the audience immediately.',
      },
      {
        title: 'The Stakes',
        description:
          'Make it clear what you stood to gain or lose. Why did this matter to you?',
      },
      {
        title: 'The Change',
        description:
          'The story must feature a transformation. You cannot be the exact same person at the end as you were at the beginning.',
      },
      {
        title: 'Vulnerability',
        description:
          'Share your failures, fears, and flaws. This builds empathy.',
      },
      {
        title: 'The Landing',
        description:
          'End on a resonant note. Do not explicitly state the moral; let the story speak for itself.',
      },
    ],
    bestFor: [
      'Live Storytelling',
      'Memoirs',
      'Keynote Speeches',
      'Personal Essays',
    ],
    example:
      "A speaker starts by describing the moment they were fired (Hook). They explain they needed the job to pay for their mother's care (Stakes). They admit they were arrogant and deserved it (Vulnerability). They describe starting a new, humbler path (Change), ending with a quiet moment of realization (Landing).",
  },
  {
    id: 'in-medias-res',
    name: 'In Medias Res',
    category: 'Creative & Narrative',
    origin: 'Horace / Classical Literature',
    summary:
      'Starting a story "in the midst of things," dropping the audience directly into the action.',
    description:
      'Latin for "into the middle of things," this technique bypasses lengthy exposition and drops the audience right into a crucial, high-tension moment. The backstory and context are then filled in later through dialogue, flashbacks, or non-linear storytelling. It is a powerful way to immediately hook an audience\'s attention.',
    steps: [
      {
        title: 'The Cold Open',
        description:
          'Start in the middle of a high-stakes scene, conflict, or confusing situation.',
      },
      {
        title: 'The Hook',
        description: 'The audience asks, "How did we get here?"',
      },
      {
        title: 'The Backfill',
        description:
          'Use flashbacks, dialogue, or narrative catch-up to explain the context.',
      },
      {
        title: 'The Continuation',
        description:
          'The story catches up to the opening moment and moves forward to the climax.',
      },
    ],
    bestFor: [
      'Thrillers',
      'Pilot Episodes',
      'Action Movies',
      'Journalism Features',
    ],
    example:
      'Breaking Bad Pilot: Starts with Walter White in his underwear, driving an RV wildly through the desert with unconscious bodies in the back, recording a goodbye tape to his family. Then, it cuts to "Three Weeks Earlier" to explain how a mild-mannered chemistry teacher ended up there.',
  },
  {
    id: 'snowflake-method',
    name: 'The Snowflake Method',
    category: 'Creative & Narrative',
    origin: 'Randy Ingermanson',
    summary:
      'A fractal approach to writing a novel, starting with a single sentence and expanding outward.',
    description:
      'Created by software engineer and author Randy Ingermanson, the Snowflake Method applies the concept of fractals to creative writing. Instead of writing linearly from chapter one, you start with a core concept and iteratively expand it. This ensures the core story is solid before you spend months writing 80,000 words.',
    steps: [
      {
        title: 'One-Sentence Summary',
        description: 'Write a 15-word summary of the entire story.',
      },
      {
        title: 'One-Paragraph Summary',
        description:
          'Expand the sentence into a paragraph detailing the setup, major disasters, and ending.',
      },
      {
        title: 'Character Summaries',
        description:
          'Write a one-page summary for each major character (motivation, goal, conflict, epiphany).',
      },
      {
        title: 'Expand the Paragraph',
        description:
          'Turn each sentence of your summary paragraph into a full paragraph.',
      },
      {
        title: 'Scene List',
        description:
          'Create a spreadsheet listing every scene needed to tell the story.',
      },
      {
        title: 'First Draft',
        description: 'Write the actual prose based on the detailed scene list.',
      },
    ],
    bestFor: ['Novel Writing', 'Series Planning', 'Complex Worldbuilding'],
    example:
      "Step 1: \"A rogue physicist travels back in time to kill the man who invented the time machine.\" Step 2: Expands to include the physicist's motive, the obstacles in the past, and the twist ending. Step 3: Details the physicist's internal trauma and the inventor's naive optimism.",
  },

  // 🎤 Persuasion & Public Speaking
  {
    id: 'monroes-motivated-sequence',
    name: "Monroe's Motivated Sequence",
    category: 'Persuasion & Public Speaking',
    origin: 'Alan H. Monroe',
    summary:
      'A five-step framework for organizing persuasive speeches that inspire people to take action.',
    description:
      'Developed in the 1930s by Purdue University professor Alan Monroe, this sequence is based on the psychology of persuasion. It is widely used in political speeches, infomercials, and sales presentations. It works by making the audience feel a specific need and then providing a way to satisfy that need, culminating in a visualization of the future.',
    steps: [
      {
        title: 'Attention',
        description:
          "Grab the audience's attention with a startling statement, story, or question.",
      },
      {
        title: 'Need',
        description:
          'Convince the audience there is a serious problem that affects them directly. Create a sense of urgency.',
      },
      {
        title: 'Satisfaction',
        description:
          'Introduce your solution. Explain how it works and how it solves the specific need you just established.',
      },
      {
        title: 'Visualization',
        description:
          "Paint a vivid picture of the future. Show them what life looks like if they adopt your solution, and what it looks like if they don't.",
      },
      {
        title: 'Action',
        description:
          'Tell the audience exactly what they need to do right now to be part of the solution.',
      },
    ],
    bestFor: [
      'Keynote Speeches',
      'Fundraising',
      'Political Campaigns',
      'Pitching Ideas',
    ],
    example:
      '"Look at this photo of a plastic-filled ocean." (Attention). "Microplastics are now in our drinking water and food supply." (Need). "Our new filtration system removes 99% of microplastics from your home tap." (Satisfaction). "Imagine drinking glass after glass knowing your family is safe from unseen toxins." (Visualization). "Order your filter today for 20% off." (Action).',
  },
  {
    id: 'rhetorical-triangle',
    name: 'The Rhetorical Triangle',
    category: 'Persuasion & Public Speaking',
    origin: 'Aristotle',
    summary:
      'The three pillars of persuasion: Ethos (credibility), Pathos (emotion), and Logos (logic).',
    description:
      "Aristotle's foundational theory of rhetoric states that a truly persuasive argument must balance three elements. If you rely only on logic (Logos), you may be dry and uninspiring. If you rely only on emotion (Pathos), you may seem manipulative. If you rely only on authority (Ethos), you may seem arrogant. The best communicators weave all three together.",
    steps: [
      {
        title: 'Ethos (Credibility)',
        description:
          'Establish your authority, character, and trustworthiness. Why should the audience listen to you?',
      },
      {
        title: 'Pathos (Emotion)',
        description:
          "Appeal to the audience's emotions, values, and imagination. Make them feel something.",
      },
      {
        title: 'Logos (Logic)',
        description:
          'Provide facts, data, statistics, and logical reasoning to support your argument.',
      },
    ],
    bestFor: [
      'Debates',
      'Opinion Editorials',
      'Courtroom Arguments',
      'Leadership Communication',
    ],
    example:
      'A doctor advocating for a new health policy: "As a practicing oncologist for 20 years..." (Ethos). "...I have held the hands of too many mothers who couldn\'t afford treatment..." (Pathos). "...and studies show this policy will reduce healthcare costs by 15% over a decade." (Logos).',
  },
  {
    id: 'rule-of-three',
    name: 'The Rule of Three',
    category: 'Persuasion & Public Speaking',
    origin: 'Classical Rhetoric',
    summary:
      'A principle suggesting that things that come in threes are inherently funnier, more satisfying, and more memorable.',
    description:
      'The human brain is a pattern-recognition machine, and three is the smallest number required to create a pattern. Information presented in groups of three sticks in our heads better than other clusters. This principle is used in storytelling (Three Little Pigs), slogans ("Just Do It" - wait, no, "Veni, Vidi, Vici"), and speechwriting to create rhythm and impact.',
    steps: [
      { title: 'Setup', description: 'Establish the first element or idea.' },
      {
        title: 'Reinforcement',
        description: 'Provide a second element that builds a pattern.',
      },
      {
        title: 'Payoff / Twist',
        description:
          'Deliver the third element to complete the pattern, or subvert it for comedic/dramatic effect.',
      },
    ],
    bestFor: ['Slogans', 'Speechwriting', 'Comedy', 'Presentation Slides'],
    example:
      '"Blood, sweat, and tears." / "Stop, drop, and roll." / "Life, liberty, and the pursuit of happiness." / Comedic twist: "I can promise you three things: hard work, dedication, and a complete lack of understanding of what I\'m doing."',
  },
  {
    id: 'nested-loops',
    name: 'Nested Loops',
    category: 'Persuasion & Public Speaking',
    origin: 'Neuro-Linguistic Programming (NLP)',
    summary:
      'Opening multiple stories within each other and closing them in reverse order to maintain rapt attention.',
    description:
      'Also known as the "Inception" method of storytelling. You start Story A, but before the climax, you get distracted and start Story B. Before Story B finishes, you start Story C. You finish C, then finish B, then finish A. This creates open loops in the listener\'s brain (the Zeigarnik effect), forcing them to pay attention until all loops are closed.',
    steps: [
      {
        title: 'Open Loop A',
        description: 'Start your main, overarching story.',
      },
      {
        title: 'Open Loop B',
        description: 'Interrupt A with a related, supporting story.',
      },
      {
        title: 'Core Message (C)',
        description:
          'Deliver the central lesson, data point, or punchline in the innermost loop.',
      },
      { title: 'Close Loop B', description: 'Finish the supporting story.' },
      {
        title: 'Close Loop A',
        description:
          'Return to the main story and deliver the final, satisfying conclusion.',
      },
    ],
    bestFor: ['Long Keynotes', 'Webinars', 'Podcasts', 'Hypnotherapy'],
    example:
      'A speaker starts telling a story about a disastrous flight (A). While describing the turbulence, they mention a conversation they had with a mentor years ago about fear (B). They deliver the core lesson about controlling your mindset (C). They finish the mentor story (B). They finally reveal how the flight landed safely (A).',
  },

  // 📊 Data & Presentation
  {
    id: 'data-storytelling',
    name: 'Data Storytelling Framework',
    category: 'Data & Presentation',
    origin: 'Brent Dykes / Modern Analytics',
    summary:
      'The intersection of Data, Narrative, and Visuals to drive change and decision-making.',
    description:
      'Data alone rarely inspires action. This framework asserts that effective data communication requires three overlapping elements. Data provides the foundation of truth. Narrative provides the context and emotional connection. Visuals provide clarity and highlight the insights. When all three combine, you get a compelling data story that drives change.',
    steps: [
      {
        title: 'Data',
        description:
          "The accurate, analyzed facts and insights. (Without narrative, it's just a spreadsheet).",
      },
      {
        title: 'Narrative',
        description:
          'The context, the "why," and the human element. (Without data, it\'s just an opinion).',
      },
      {
        title: 'Visuals',
        description:
          'Charts, graphs, and design that make the data instantly understandable. (Without narrative, they are just pretty pictures).',
      },
      {
        title: 'The Intersection',
        description:
          'Combine them: Use the narrative to explain the data, and the visuals to highlight the narrative.',
      },
    ],
    bestFor: [
      'Quarterly Business Reviews',
      'Data Science Presentations',
      'Infographics',
    ],
    example:
      'Instead of showing a spreadsheet of declining sales (Data), you show a line chart with a sharp drop (Visual) and tell the story of how a specific supply chain disruption in March caused stockouts for your most loyal customers, and propose a new vendor strategy (Narrative).',
  },
  {
    id: 'sparkline',
    name: 'The Sparkline',
    category: 'Data & Presentation',
    origin: 'Nancy Duarte',
    summary:
      'A presentation structure that constantly contrasts "what is" with "what could be."',
    description:
      'Developed by presentation expert Nancy Duarte after analyzing hundreds of the greatest speeches in history (like MLK\'s "I Have a Dream" and Steve Jobs\' iPhone launch). The Sparkline maps out a presentation as a continuous oscillation between the current, flawed reality and the brilliant, possible future, ending with a "new bliss."',
    steps: [
      {
        title: 'What Is',
        description:
          'Describe the current state of affairs. Highlight the pain points, inefficiencies, or injustices.',
      },
      {
        title: 'What Could Be',
        description:
          'Contrast it immediately with a vision of the future where the problem is solved.',
      },
      {
        title: 'Oscillation',
        description:
          'Move back and forth between "What Is" and "What Could Be" to build tension and desire.',
      },
      {
        title: 'The Call to Action',
        description: 'Tell the audience what they must do to cross the gap.',
      },
      {
        title: 'New Bliss',
        description:
          'End by describing the utopian state that will exist once the action is taken.',
      },
    ],
    bestFor: ['Product Launches', 'Visionary Keynotes', 'Change Management'],
    example:
      'Steve Jobs launching the iPhone: "Smartphones today are not smart and not easy to use." (What Is). "What if a phone was as easy to use as a Mac?" (What Could Be). He oscillates between the clunky plastic keyboards of competitors (What Is) and the sleek multi-touch glass of the iPhone (What Could Be).',
  },
  {
    id: 'explanation-scale',
    name: 'The Explanation Scale',
    category: 'Data & Presentation',
    origin: 'Common Communication Theory',
    summary:
      'A method for explaining complex technical concepts by moving from high-level context down to granular details.',
    description:
      'When technical experts present to non-technical audiences, they often start in the weeds (the details), losing the audience immediately. The Explanation Scale forces you to zoom out. You must establish the broad context first, explain the core concept using analogies, and only then dive into the technical details for those who need them.',
    steps: [
      {
        title: 'Context (The Forest)',
        description:
          'Why are we talking about this? What is the big picture business goal or problem?',
      },
      {
        title: 'Concept (The Trees)',
        description:
          'What is the solution in plain English? Use analogies, metaphors, and simple diagrams.',
      },
      {
        title: 'Details (The Leaves)',
        description:
          'How does it actually work? Provide the technical specs, data architecture, or granular code.',
      },
    ],
    bestFor: [
      'Technical Demos',
      'Engineering Pitching to Business',
      'Product Documentation',
    ],
    example:
      'Explaining an API to a CEO: "We need our website to talk to our shipping provider to show live tracking." (Context). "Think of an API like a waiter at a restaurant taking your order to the kitchen and bringing the food back." (Concept). "We will use RESTful endpoints with JSON payloads authenticated via OAuth." (Details).',
  },
]
